import { LayoutGeometryHelper as G } from './layout-geometry-helper';
import type { LeafSide } from './leaf-column-helper';
import type { Rect, ShareLabel } from './structure-layout.types';
import { TextMeasureHelper, type TextMeasurer } from './text-measure-helper';

export interface LabelText {
  text: string;
  width: number;
}

class LabelPlacementHelper {
  static getLabelText(
    text: string,
    measurer: Readonly<TextMeasurer>
  ): LabelText {
    return {
      text,
      width:
        Math.ceil(measurer.measure(text, TextMeasureHelper.LABEL_FONT)) +
        2 * G.LABEL_PADDING_X,
    };
  }

  static getIndent(maxLabelWidth: number): number {
    return maxLabelWidth > 0
      ? maxLabelWidth + G.CORNER_RADIUS + 2 * G.LABEL_GAP
      : 0;
  }

  static onBranch(
    relationId: string,
    label: Readonly<LabelText>,
    trunkX: number,
    centerY: number,
    side: LeafSide
  ): ShareLabel {
    const offset = G.CORNER_RADIUS + G.LABEL_GAP;
    const x =
      side === 'right' ? trunkX + offset : trunkX - offset - label.width;
    return LabelPlacementHelper.create(
      relationId,
      label,
      x,
      centerY - G.LABEL_LIFT - G.LABEL_HEIGHT
    );
  }

  static besideDrop(
    relationId: string,
    label: Readonly<LabelText>,
    lineX: number,
    top: number,
    bottom: number
  ): ShareLabel {
    return LabelPlacementHelper.create(
      relationId,
      label,
      lineX + G.LABEL_CLEARANCE,
      Math.round((top + bottom - G.LABEL_HEIGHT) / 2)
    );
  }

  static atOwner(
    relationId: string,
    label: Readonly<LabelText>,
    owner: Readonly<Rect>
  ): ShareLabel {
    return LabelPlacementHelper.create(
      relationId,
      label,
      owner.x + owner.width + G.LABEL_GAP,
      owner.y + owner.height / 2 - G.LABEL_LIFT - G.LABEL_HEIGHT
    );
  }

  private static create(
    relationId: string,
    label: Readonly<LabelText>,
    x: number,
    y: number
  ): ShareLabel {
    return {
      id: `label:${relationId}`,
      relationId,
      text: label.text,
      x,
      y,
      width: label.width,
      height: G.LABEL_HEIGHT,
    };
  }
}

export { LabelPlacementHelper };
